import {announce} from '@github-ui/aria-live'
import {useMutation, useQueryClient} from '@github-ui/react-query'

import {LABELS} from '../constants'
import {repositorySavedViewEndpoints, useSavedViewEndpoints} from '../contexts/SavedViewEndpointsContext'
import {savedViewPinsQueryKey} from '../queries'
import type {SavedView, SetSavedViewPinnedResponse, SetSavedViewPinnedVariables} from '../types'
import {
  invalidateSavedView,
  invalidateSavedViewsList,
  optimisticallySetSavedViewPinned,
  pinnedMutationErrorMessage,
  primeSavedViewCache,
  restoreSavedViewPinnedSnapshot,
  type SavedViewMutationOptions,
  type SavedViewPinnedSnapshot,
  sendSavedViewMutationJson,
} from '../utils/saved-view-mutations'
import {SAVED_VIEW_PIN_SOURCES, type SavedViewPinSource, useSavedViewAnalytics} from './use-saved-view-analytics'

const pinnedErrorLabel = (pinned: boolean, viewName: string) =>
  pinned ? LABELS.views.pinError(viewName) : LABELS.views.unpinError(viewName)

const SET_SAVED_VIEW_PINNED_MUTATION_KEY = 'set-saved-view-pinned'

export interface SetSavedViewPinnedCallbacks {
  onPinMutate?: (variables: SetSavedViewPinnedVariables) => void
  onPinSuccess?: (variables: SetSavedViewPinnedVariables) => void
  onPinError?: (args: {error: Error} & SetSavedViewPinnedVariables) => void
}

export function useSetSavedViewPinnedMutation({
  owner,
  repo,
  source = SAVED_VIEW_PIN_SOURCES.individualViewActionsMenu,
  pinLimitForView,
  onPinMutate = () => {},
  onPinSuccess = () => {},
  onPinError = () => {},
}: SavedViewMutationOptions & {
  source?: SavedViewPinSource
  pinLimitForView?: (view: SavedView) => number
} & SetSavedViewPinnedCallbacks) {
  const queryClient = useQueryClient()
  const {trackViewPinned, trackViewUnpinned} = useSavedViewAnalytics()
  const {basePath, listUrl} = useSavedViewEndpoints(owner, repo)
  const repositoryBasePath = repositorySavedViewEndpoints(owner, repo).basePath
  const mutationKey =
    basePath === repositoryBasePath
      ? ([SET_SAVED_VIEW_PINNED_MUTATION_KEY, owner, repo] as const)
      : ([SET_SAVED_VIEW_PINNED_MUTATION_KEY, owner, repo, basePath] as const)
  const cacheTargets = {queryClient, owner, repo, basePath, listUrl}

  return useMutation<SetSavedViewPinnedResponse, Error, SetSavedViewPinnedVariables, SavedViewPinnedSnapshot>({
    mutationKey,
    mutationFn: ({id, name, pinned}) =>
      sendSavedViewMutationJson<SetSavedViewPinnedResponse>(
        `${basePath}/${encodeURIComponent(id)}/pinned.json`,
        {method: 'PUT', body: {pinned}},
        pinnedErrorLabel(pinned, name),
      ),
    onMutate: variables => {
      onPinMutate(variables)
      return optimisticallySetSavedViewPinned(cacheTargets, variables)
    },
    onSuccess: ({savedView, pinnedViews}, variables, context) => {
      primeSavedViewCache(cacheTargets, savedView)
      const isLastPendingMutation = queryClient.isMutating({mutationKey}) <= 1
      const pinnedViewsKey = savedViewPinsQueryKey({owner, repo, listUrl})
      const pinsUnchangedSinceMutation =
        queryClient.getQueryData<SavedView[]>(pinnedViewsKey) === context?.optimisticPinnedViews
      if (pinnedViews && isLastPendingMutation && pinsUnchangedSinceMutation) {
        queryClient.setQueryData<SavedView[]>(pinnedViewsKey, pinnedViews)
      }
      announce(savedView.pinned ? LABELS.views.pinSuccess(savedView.name) : LABELS.views.unpinSuccess(savedView.name))
      if (savedView.pinned) {
        trackViewPinned(savedView.id, savedView.visibility, source)
      } else {
        trackViewUnpinned(savedView.id, savedView.visibility, source)
      }
      onPinSuccess(variables)
    },
    onError: (error, variables, context) => {
      if (context) restoreSavedViewPinnedSnapshot(cacheTargets, context)
      announce(
        pinnedMutationErrorMessage(
          error,
          variables.pinned,
          variables.name,
          variables.view ? pinLimitForView?.(variables.view) : undefined,
        ),
        {assertive: true},
      )
      onPinError({error, ...variables})
    },
    onSettled: (_data, _error, variables) => {
      if (queryClient.isMutating({mutationKey}) <= 1) {
        invalidateSavedView(cacheTargets, variables.id)
        invalidateSavedViewsList(cacheTargets)
      }
    },
  })
}
